import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/store/organization.store';
import { queryKeys } from './query-keys';
import { extractOrgSlug } from '@/utils/org-paths';

let reloadInProgress = false;
/**
 * Hook to manage React Query cache invalidation when organization changes
 * This ensures all queries are cleared immediately when switching organizations
 * 
 * Uses removeQueries() instead of invalidateQueries() to immediately clear cached data
 * and prevent stale data from showing when switching between organizations
 */
export function useOrganizationQueryInvalidation() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const currentOrganization = useOrganizationStore((state) => state.currentOrganization);
  const previousOrgIdRef = useRef<string | null>(null);
  const stableOrgIdRef = useRef<string | null>(null);
  const stableSlugRef = useRef<string | null>(extractOrgSlug(location.pathname));
  const pendingOrgIdRef = useRef<string | null>(null);
  const pendingSlugRef = useRef<string | null>(null);

  useEffect(() => {
    const currentOrgId = currentOrganization?.id ?? null;
    const previousOrgId = previousOrgIdRef.current;
    const currentSlug = extractOrgSlug(location.pathname);

    // Detect actual org change (from one org to another, or to null)
    // Skip on initial mount (previousOrgId === null && currentOrgId !== null)
    if (previousOrgId !== null && previousOrgId !== currentOrgId) {
      
      // Remove all org-specific queries to ensure fresh data
      // removeQueries() immediately removes the cache, unlike invalidateQueries() which only marks as stale
      const keysToRemove = [
        // Centralized query keys
        queryKeys.projects.all,
        queryKeys.members.all,
        queryKeys.entities.all,
        queryKeys.tasks.all,
        queryKeys.leads.all,
        queryKeys.deals.all,
        queryKeys.invoices.all,
        queryKeys.contracts.all,
        queryKeys.events.all,
        queryKeys.resources.all,
        queryKeys.sprints.all,
        queryKeys.threads.all,
        queryKeys.inventory.all,
        queryKeys.suppliers.all,
        queryKeys.directory.all,
        queryKeys.timeEntries.all,
        queryKeys.memberResources.all,
        queryKeys.customFields.all,
        queryKeys.customCollections.all,
        queryKeys.permissions.all,
        queryKeys.crmStages.all,
        queryKeys.notifications.all,
        queryKeys.organizationSettings.all,
        queryKeys.projectMembers.all,
        queryKeys.organizationInvites.all,
        queryKeys.userProfile.all,
        
        // Standalone query keys (not in centralized queryKeys)
        ['subtasks'],
        ['my-tasks'],
        ['task'],
        ['tasks'],
        ['entities'],
        ['project'],
        ['entity'],
        ['column-preferences'],
        ['user-preferences'],
        ['custom-fields'],
        ['task-groups'],
        ['task-dependencies'],
        ['project-settings'],
        ['permission-sets'],
        ['time-entries'],
        
        // AI-related queries
        ['ai-prompts'],
        ['ai-prompts-favorites'],
        ['ai-prompts-recent'],
        ['ai-usage-summary'],
        ['ai-usage-details'],
        ['ai-usage-paginated'],
        ['ai-models'],
        ['ai-model-preference'],
        
        // Integration queries
        ['integrations'],
        ['bulk-module-tags'],
        ['tag-definitions'],
      ];

      keysToRemove.forEach((key) => {
        queryClient.removeQueries({ queryKey: key });
      });
    }

    if (!currentSlug || currentSlug === stableSlugRef.current) {
      pendingSlugRef.current = null;
    } else {
      pendingSlugRef.current = currentSlug;
    }

    if (!currentOrgId || currentOrgId === stableOrgIdRef.current) {
      pendingOrgIdRef.current = null;
    } else {
      pendingOrgIdRef.current = currentOrgId;
    }

    if (!stableOrgIdRef.current && currentOrgId) {
      stableOrgIdRef.current = currentOrgId;
      pendingOrgIdRef.current = null;
    }

    if (
      pendingOrgIdRef.current &&
      pendingSlugRef.current &&
      stableOrgIdRef.current &&
      pendingOrgIdRef.current !== stableOrgIdRef.current
    ) {
      // Hard reload to flush in-memory org state and prevent cross-org mixing.
      if (typeof window !== 'undefined') {
        if (reloadInProgress) {
          return;
        }
        reloadInProgress = true;
        window.location.reload();
      }
      return;
    }

    if (!pendingOrgIdRef.current && currentOrgId) {
      stableOrgIdRef.current = currentOrgId;
    }

    if (!pendingSlugRef.current) {
      stableSlugRef.current = currentSlug;
    }

    // Update ref for next comparison
    previousOrgIdRef.current = currentOrgId;
  }, [currentOrganization?.id, queryClient, location.pathname]);
}
